import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Fade,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar
} from '@mui/material';
import {
  AutoAwesome,
  Send,
  CheckCircle,
  ArrowBack,
  VerifiedUser,
  Refresh,
  AttachFile,
  Close,
  Star,
  LocationOn,
  Image,
  Delete,
  Shield
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brand, text, bg, border } from '../../theme/colors';
import VerifierHireDocumentPreview from './VerifierHireDocumentPreview';
import DialogCloseButton from '../common/DialogCloseButton';

/**
 * AIVerifierHiringDialog
 * Conversational AI dialog for finding and hiring verifiers
 * Multi-step interview that sends quote requests to top matches
 */
const AIVerifierHiringDialog = ({ open, onClose, onComplete }) => {
  const { getApiClient, user } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [improving, setImproving] = useState(false);
  const [matchedVerifiers, setMatchedVerifiers] = useState([]);
  const [selectedVerifiers, setSelectedVerifiers] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showVerifierSelection, setShowVerifierSelection] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Filter states for verifier selection
  const [filterSearch, setFilterSearch] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showPreviousOnly, setShowPreviousOnly] = useState(false);
  const [previousVerifiers, setPreviousVerifiers] = useState([]);

  // Filter verifiers based on search, location, type, and previous work history
  const filteredVerifiers = matchedVerifiers.filter(verifier => {
    const name = (verifier.full_name || verifier.first_name + ' ' + verifier.last_name || '').toLowerCase();
    const location = (verifier.city || verifier.location || '').toLowerCase();
    const type = (verifier.tier || verifier.verifier_type || '').toLowerCase();
    const verifierId = verifier.id || verifier.user_id;
    
    const matchesSearch = !filterSearch || name.includes(filterSearch.toLowerCase());
    const matchesLocation = !filterLocation || location.includes(filterLocation.toLowerCase());
    const matchesType = !filterType || type.includes(filterType.toLowerCase());
    const matchesPrevious = !showPreviousOnly || previousVerifiers.includes(verifierId);
    
    return matchesSearch && matchesLocation && matchesType && matchesPrevious;
  });

  // Conversational questions for verifier hiring
  const questions = [
    {
      id: 'verificationType',
      text: `What type of verification do you need a quote for?`,
      placeholder: 'e.g., Building inspection, Quality control, Code compliance...',
      examples: ['Building Inspection', 'Quality Control', 'Code Compliance', 'Structural Assessment', 'Safety Inspection', 'Final Inspection'],
      allowAI: true // Enable AI improvement
    },
    {
      id: 'location',
      text: 'Where is the project located?',
      placeholder: 'e.g., Harare, Bulawayo, Mutare...'
    },
    {
      id: 'timeline',
      text: 'When do you need this verification completed?',
      placeholder: 'e.g., Within 3 days, Next week, Flexible...',
      examples: ['Urgent (within 3 days)', 'Within 1 week', 'Within 2 weeks', 'Flexible']
    },
    {
      id: 'budget',
      text: "What's your estimated budget? (in USD)",
      placeholder: 'e.g., 500, 1000-2000, Open to proposals...',
      type: 'text'
    },
    {
      id: 'description',
      text: 'Describe the verification needs in detail so verifiers can provide accurate quotes:',
      placeholder: 'e.g., Foundation integrity, electrical installations, plumbing systems, overall structural safety...',
      multiline: true,
      rows: 4,
      allowAI: true, // Enable AI improvement
      allowFiles: true // Enable file uploads
    }
  ];

  const currentQuestion = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  // Snackbar helper
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Improve with AI (exact same as bid generator)
  const handleImproveWithAI = async () => {
    if (!currentAnswer.trim()) {
      showSnackbar('Please enter some content first', 'warning');
      return;
    }

    setImproving(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/bids/ai/improve-section', {
        section: currentQuestion.id === 'verificationType' ? 'verification_type' : 'verification_description',
        currentContent: currentAnswer,
        improvementRequest: ''
      });

      setCurrentAnswer(response.data.data.improvedContent);
      showSnackbar('✨ Content improved!', 'success');
    } catch (error) {
      console.error('Error improving content:', error);
      showSnackbar('Failed to improve content. Please try again.', 'error');
    } finally {
      setImproving(false);
    }
  };

  // File upload handler
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + uploadedFiles.length > 10) {
      showSnackbar('Maximum 10 files allowed', 'warning');
      return;
    }
    setUploadedFiles([...uploadedFiles, ...files]);
    showSnackbar(`${files.length} file(s) added`, 'success');
  };

  // Remove file handler
  const handleRemoveFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  // Toggle verifier selection
  const handleToggleVerifier = (verifierId) => {
    setSelectedVerifiers(prev =>
      prev.includes(verifierId)
        ? prev.filter(id => id !== verifierId)
        : [...prev, verifierId]
    );
  };

  const handleNext = async () => {
    if (!currentAnswer.trim() && !currentQuestion.optional) {
      setError('Please provide an answer to continue');
      return;
    }

    // Save answer
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: currentAnswer.trim()
    };
    setAnswers(newAnswers);
    setCurrentAnswer('');
    setError(null);

    // Show AI typing animation
    setAiTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setAiTyping(false);

    // Move to next question or submit
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      // All questions answered
      // If we already have verifiers loaded, just show preview
      if (matchedVerifiers.length > 0) {
        setShowPreview(true);
      } else {
        // First time - fetch verifiers
        await handleSubmit(newAnswers);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      // Restore previous answer
      setCurrentAnswer(answers[questions[step - 1].id] || '');
    }
  };

  const handleSubmit = async (finalAnswers) => {
    setIsLoading(true);
    setError(null);

    try {
      const apiClient = getApiClient();
      
      // Fetch all available verifiers
      const response = await apiClient.get('/verifiers');
      const verifiers = response.data.data || response.data || [];

      if (verifiers.length === 0) {
        setError('No verifiers available at the moment. Please try again later.');
        setIsLoading(false);
        return;
      }

      // Try to fetch verifiers user has worked with before
      try {
        const historyResponse = await apiClient.get('/hires/verifier-history');
        const history = historyResponse.data.data || historyResponse.data || [];
        setPreviousVerifiers(history.map(v => v.verifier_id || v.id));
      } catch (historyErr) {
        console.log('Could not load verifier history:', historyErr.message);
        setPreviousVerifiers([]);
      }

      // Show all verifiers for user selection
      setMatchedVerifiers(verifiers);
      setSelectedVerifiers([]); // Don't pre-select, let user choose
      setShowPreview(true);
      setIsLoading(false);

    } catch (err) {
      console.error('Error loading verifiers:', err);
      setError(err.response?.data?.message || 'Failed to load verifiers. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSendRequests = async () => {
    // Close preview and show verifier selection
    setShowPreview(false);
    setShowVerifierSelection(true);
  };

  const handleSubmitDocument = async () => {
    if (selectedVerifiers.length === 0) {
      showSnackbar('Please select at least one verifier', 'warning');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiClient = getApiClient();
      // Send quote requests to selected verifiers
      const quoteRequests = selectedVerifiers.map(verifierId =>
        apiClient.post('/contractor/verifier-hires', {
          verifierId: verifierId,
          verificationType: answers.verificationType,
          location: answers.location,
          budget: answers.budget,
          timeline: answers.timeline,
          description: answers.description,
          files: uploadedFiles
        })
      );

      await Promise.all(quoteRequests);

      setSuccess(true);
      showSnackbar(`Quote requests sent to ${selectedVerifiers.length} verifiers!`, 'success');

      setTimeout(() => {
        if (onComplete) {
          onComplete({
            verifiers: matchedVerifiers.filter(c => selectedVerifiers.includes(c.id)),
            answers: answers
          });
        }
        handleClose();
      }, 3000);

    } catch (err) {
      console.error('Error sending quote requests:', err);
      setError(err.response?.data?.message || 'Failed to send requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setStep(0);
      setAnswers({});
      setCurrentAnswer('');
      setError(null);
      setSuccess(false);
      setMatchedVerifiers([]);
      setSelectedVerifiers([]);
      setShowVerifierSelection(false);
      setUploadedFiles([]);
      onClose();
    }
  };

  const handleExampleClick = (example) => {
    setCurrentAnswer(example);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '600px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      {/* Header */}
      <DialogTitle
        sx={{
          background: brand.gradient,
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <VerifiedUser sx={{ fontSize: 32 }} />
          <Box flexGrow={1}>
            <Typography variant="h5" fontWeight={700}>
              Request Verifier Quotes
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Describe your verification needs and receive quotes from qualified verifiers
            </Typography>
          </Box>
        </Box>

        {/* Progress Bar */}
        {!showVerifierSelection && !success && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'white',
                  borderRadius: 3
                }
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.9 }}>
              Step {step + 1} of {questions.length}
            </Typography>
          </Box>
        )}
        {showVerifierSelection && !success && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Select verifiers to send your quote request to
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Success Message */}
        {success && (
          <Fade in={success}>
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{ mb: 3 }}
            >
              <Typography variant="h6" gutterBottom>
                🎉 Quote request sent!
              </Typography>
              <Typography variant="body2">
                Your verification request has been sent to {selectedVerifiers.length} verifier{selectedVerifiers.length > 1 ? 's' : ''}.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Expect to receive quotes within 24-48 hours.
              </Typography>
            </Alert>
          </Fade>
        )}

        {/* Error Alert */}
        {error && !success && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Verifier Selection Screen */}
        {showVerifierSelection && !success && (
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Choose Who Receives Your Quote Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the verifiers you'd like to send your quote request to. You can choose as many as you'd like.
            </Typography>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Location..."
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                    InputProps={{
                      startAdornment: <LocationOn sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type (e.g. Professional)..."
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    InputProps={{
                      startAdornment: <Shield sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPreviousOnly}
                        onChange={(e) => setShowPreviousOnly(e.target.checked)}
                        size="small"
                        color="success"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                        Worked with before
                      </Typography>
                    }
                    sx={{ ml: 0.5 }}
                  />
                </Grid>
              </Grid>
              {showPreviousOnly && previousVerifiers.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Showing verifiers you've worked with before
                </Typography>
              )}
              {showPreviousOnly && previousVerifiers.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  You haven't worked with any verifiers yet
                </Typography>
              )}
            </Paper>

            {/* Summary Box */}
            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#f3e5f5',
                border: `1px solid ${brand.primary}`,
                borderRadius: 2,
                p: 2,
                mb: 3
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body1" fontWeight={600} color={brand.secondary}>
                    {selectedVerifiers.length} verifier{selectedVerifiers.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedVerifiers.length === 0 && 'Please select at least one verifier to continue'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredVerifiers.length} of {matchedVerifiers.length}
                </Typography>
              </Box>
            </Paper>

            {/* Verifiers Grid */}
            <Grid container spacing={2}>
              {filteredVerifiers.map((verifier) => (
                <Grid item xs={12} sm={6} key={verifier.id}>
                  <Card
                    sx={{
                      border: selectedVerifiers.includes(verifier.id)
                        ? `2px solid ${brand.primary}`
                        : `1px solid ${border.default}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: brand.primary
                      }
                    }}
                    onClick={() => handleToggleVerifier(verifier.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <Checkbox
                          checked={selectedVerifiers.includes(verifier.id)}
                          onChange={() => handleToggleVerifier(verifier.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            mt: -1,
                            ml: -1
                          }}
                        />
                        <Avatar
                          src={verifier.profile_image_url || verifier.avatar}
                          alt={verifier.name || verifier.full_name}
                          sx={{ width: 48, height: 48 }}
                        >
                          {(verifier.name || verifier.full_name)?.charAt(0)}
                        </Avatar>
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {verifier.full_name || verifier.name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                            {verifier.tier && (
                              <Chip
                                label={verifier.tier.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: brand.primary,
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.65rem',
                                  height: 18
                                }}
                              />
                            )}
                            {verifier.verifier_type && (
                              <Chip
                                label={verifier.verifier_type}
                                size="small"
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: brand.secondary,
                                  fontSize: '0.65rem',
                                  height: 18
                                }}
                              />
                            )}
                            {previousVerifiers.includes(verifier.id || verifier.user_id) && (
                              <Chip
                                label="Worked With"
                                size="small"
                                sx={{
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  fontSize: '0.65rem',
                                  height: 18,
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                            {(verifier.avg_rating > 0 || verifier.rating > 0) && (
                              <Box display="flex" alignItems="center">
                                <Star sx={{ fontSize: 16, color: '#ffc107', mr: 0.3 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {parseFloat(verifier.avg_rating || verifier.rating).toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                            {(verifier.base_city || verifier.location) && (
                              <Box display="flex" alignItems="center">
                                <LocationOn sx={{ fontSize: 16, color: '#666', mr: 0.3 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {verifier.base_city || verifier.location}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {!success && !showVerifierSelection && (
          <>
            {/* Question */}
            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#f5f5f5',
                border: `2px solid ${brand.primary}`,
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
            >
              {/* AI Icon */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  left: 20,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: brand.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 3
                }}
              >
                <AutoAwesome sx={{ color: 'white', fontSize: 24 }} />
              </Box>

              <Typography variant="h6" sx={{ mt: 1, mb: 2, color: brand.secondary }}>
                {currentQuestion.text}
              </Typography>

              {/* Examples */}
              {currentQuestion.examples && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Quick select:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {currentQuestion.examples.map((example, index) => (
                      <Chip
                        key={index}
                        label={example}
                        onClick={() => handleExampleClick(example)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                            borderColor: brand.primary
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Answer Input */}
              <TextField
                fullWidth
                multiline
                minRows={currentQuestion.rows || 2}
                maxRows={6}
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                placeholder={currentQuestion.placeholder}
                disabled={isLoading || aiTyping || improving}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !currentQuestion.multiline && !isLoading) {
                    e.preventDefault();
                    handleNext();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    fontSize: '16px',
                    lineHeight: 1.6
                  }
                }}
              />

              {/* Improve with AI Button (for questions 1 & 5) */}
              {currentQuestion.allowAI && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    startIcon={improving ? <CircularProgress size={16} /> : <AutoAwesome />}
                    onClick={handleImproveWithAI}
                    disabled={!currentAnswer.trim() || improving || isLoading}
                  >
                    {improving ? 'Improving...' : 'Improve with AI'}
                  </Button>
                </Box>
              )}

              {/* File Upload (for question 5 - description) */}
              {currentQuestion.allowFiles && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFile />}
                    size="small"
                    disabled={isLoading}
                  >
                    Upload Photos
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleFileUpload}
                    />
                  </Button>

                  {/* Uploaded Files List */}
                  {uploadedFiles.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      {uploadedFiles.map((file, index) => (
                        <Chip
                          key={index}
                          icon={<Image />}
                          label={file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                          onDelete={() => handleRemoveFile(index)}
                          size="small"
                          sx={{ maxWidth: 200 }}
                        />
                      ))}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {uploadedFiles.length}/10 files uploaded
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* AI Typing Indicator */}
            {aiTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Processing your answer...
                </Typography>
              </Box>
            )}

            {/* Summary of Previous Answers */}
            {step > 0 && (
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: bg.subtle,
                  border: `1px solid ${border.default}`,
                  borderRadius: 2,
                  p: 2
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Your Answers:
                </Typography>
                {Object.keys(answers).map((key) => {
                  const question = questions.find(q => q.id === key);
                  return (
                    <Typography key={key} variant="body2" color="text.secondary">
                      <strong>{question?.text.replace('?', '')}:</strong> {answers[key]}
                    </Typography>
                  );
                })}
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${border.default}` }}>
          {/* Verifier Selection Actions */}
          {showVerifierSelection ? (
            <>
              <Button
                onClick={() => {
                  // Go back to preview, preserving all data
                  setShowVerifierSelection(false);
                  setShowPreview(true);
                }}
                disabled={isLoading}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back to Preview
              </Button>
              <Box flexGrow={1} />
              <Button
                onClick={handleClose}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitDocument}
                disabled={isLoading || selectedVerifiers.length === 0}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  background: brand.gradient,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${brand.secondary} 0%, ${brand.primaryDark} 100%)`
                  }
                }}
              >
                Send Quote Requests ({selectedVerifiers.length})
              </Button>
            </>
          ) : (
            /* Question Navigation Actions */
            <>
              <Button
                onClick={handleBack}
                disabled={step === 0 || isLoading}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
              <Box flexGrow={1} />
              <Button
                onClick={handleClose}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isLoading || aiTyping || !currentAnswer.trim()}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  background: brand.gradient,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${brand.secondary} 0%, ${brand.primaryDark} 100%)`
                  }
                }}
              >
                {step === questions.length - 1 ? 'Get Matching Verifiers' : 'Next'}
              </Button>
            </>
          )}
        </DialogActions>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Document Preview Modal */}
      {showPreview && (
        <VerifierHireDocumentPreview
          open={showPreview}
          onClose={() => {
            // Go back to last question to edit, but preserve answers
            setShowPreview(false);
            setStep(questions.length - 1);
            setCurrentAnswer(answers[questions[questions.length - 1].id] || '');
          }}
          onSubmit={handleSendRequests}
          data={{
            projectTitle: `${answers.verificationType} Verification`,
            contractorName: user?.company_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Contractor',
            verificationType: answers.verificationType,
            location: answers.location,
            projectSummary: answers.description,
            areasToVerify: answers.description,
            focusPoints: answers.description,
            complianceStandards: 'All relevant building codes and safety regulations',
            buildingCodes: 'Local and national building codes applicable to this project type',
            projectStage: 'In Progress',
            verificationPurpose: 'Quality assurance and compliance verification',
            stakeholders: 'Project owner, contractor, and regulatory authorities',
            startDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
            duration: answers.timeline,
            reportDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 2 weeks from now
            timelineUrgency: answers.timeline?.toLowerCase().includes('urgent') ? 'Urgent' : 'Standard',
            budgetMin: typeof answers.budget === 'string' && answers.budget.includes('-') 
              ? parseInt(answers.budget.split('-')[0]) 
              : parseInt(answers.budget) || 0,
            budgetMax: typeof answers.budget === 'string' && answers.budget.includes('-')
              ? parseInt(answers.budget.split('-')[1])
              : parseInt(answers.budget) * 1.2 || 0,
            feeStructure: 'Hourly rate or flat fee',
            paymentTerms: 'Payment upon report delivery',
            additionalDeliverables: 'Additional documentation as requested',
            documentsProvided: uploadedFiles.length > 0 ? `${uploadedFiles.length} file(s) attached` : 'Project plans and specifications will be provided',
            siteAccess: 'Site access will be coordinated with the verifier',
            additionalRequirements: 'Must have relevant experience in project type',
            numberOfVerifiers: selectedVerifiers.length,
            evaluationCriteria: '1. Qualifications and certifications\n2. Experience with similar projects\n3. Availability and timeline\n4. Fee structure and value\n5. References and reputation',
            termsAndConditions: `By submitting a proposal, verifiers acknowledge and agree to:\n\n• All information provided is accurate and complete\n• They have the necessary licenses, certifications, and insurance to perform the verification\n• They will maintain confidentiality of all project information\n• The contractor reserves the right to accept or reject any proposal\n• All verification work will be conducted professionally and ethically\n• Reports will comply with industry standards and be legally defensible\n• Verifier carries appropriate professional indemnity insurance`,
            responseDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
            verifierTier: 'professional' // Default to professional tier
          }}
        />
      )}
    </Dialog>
  );
};

export default AIVerifierHiringDialog;
